import jwt_decode from 'jwt-decode';

/* ------------------------------------------------------------------ */
/* Constants shared by the Pre-Moveout Inspection module               */
/* ------------------------------------------------------------------ */

export const REPORT_STATUS = {
  draft: { label: 'Draft', tone: 'draft' },
  submitted: { label: 'Submitted', tone: 'submitted' },
  vendor_assigned: { label: 'Vendor assigned', tone: 'vendor_assigned' },
  quote_received: { label: 'Quote received', tone: 'quote_received' },
  approved: { label: 'Approved', tone: 'approved' },
  completed: { label: 'Completed', tone: 'completed' },
  cancelled: { label: 'Cancelled', tone: 'cancelled' },
};

export const REPORT_STATUS_ORDER = [
  'draft',
  'submitted',
  'vendor_assigned',
  'quote_received',
  'approved',
  'completed',
  'cancelled',
];

export const ASSIGNMENT_STATUS = {
  assigned: { label: 'Awaiting quote', tone: 'submitted' },
  quoted: { label: 'Pending approval', tone: 'quote_received' },
  approved: { label: 'Approved', tone: 'approved' },
  declined: { label: 'Rejected', tone: 'cancelled' },
  cancelled: { label: 'Cancelled', tone: 'draft' },
};

export const ASSIGNMENT_KIND = {
  contractor: 'Contractor',
  cleaner: 'Cleaner',
  internal: 'Internal maintenance',
};

/** Fallback list; GET / and GET /:id also return meta.categories. */
export const REPAIR_CATEGORIES = [
  'Cleaning',
  'Painting',
  'Flooring',
  'Plumbing',
  'Electrical',
  'Appliances',
  'Doors & Locks',
  'Windows & Blinds',
  'Walls & Ceilings',
  'Fixtures & Hardware',
  'Pest Control',
  'Other',
];

export const CLEANLINESS_RATINGS = [
  { value: 'clean', label: 'Clean', icon: 'check-circle' },
  { value: 'okay', label: 'Okay', icon: 'minus-circle' },
  { value: 'dirty', label: 'Dirty', icon: 'alert-triangle' },
];

export const HISTORY_ICONS = {
  created: 'file-plus',
  edited: 'edit-2',
  submitted: 'send',
  file_added: 'paperclip',
  file_removed: 'trash-2',
  vendor_assigned: 'user-plus',
  quote_request_resent: 'repeat',
  quote_received: 'dollar-sign',
  quote_approved: 'check-circle',
  quote_declined: 'x-circle',
  assignment_cancelled: 'user-x',
  cancelled: 'slash',
  reopened: 'rotate-ccw',
  completed: 'check-square',
};

export const HISTORY_LABELS = {
  created: 'Created',
  edited: 'Edited',
  submitted: 'Submitted',
  file_added: 'File added',
  file_removed: 'File removed',
  vendor_assigned: 'Vendor assigned',
  quote_request_resent: 'Quote request resent',
  quote_received: 'Quote received',
  quote_approved: 'Quote approved',
  quote_declined: 'Quote declined',
  assignment_cancelled: 'Assignment cancelled',
  cancelled: 'Inspection cancelled',
  reopened: 'Reopened',
  completed: 'Marked completed',
};

export const ALLOWED_MEDIA_TYPES =
  'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';
export const MAX_MEDIA_SIZE = 120 * 1024 * 1024;
export const MAX_MEDIA_PER_SECTION = 10;

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

const moneyFormatter = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });

export const fmtMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return moneyFormatter.format(n);
};

/** Calendar date (no time) — always rendered in UTC so 'YYYY-MM-DD' round-trips. */
export const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/** Timestamp (audit trail, updatedAt) — rendered in the viewer's local time. */
export const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** 'HH:mm' → '2:30 PM' */
export const fmtTime = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !/^\d{1,2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
};

/** ISO (or Date) → 'YYYY-MM-DD' in UTC, for <input type="date">. */
export const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

/** Today's local calendar date as 'YYYY-MM-DD'. */
export const todayInput = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export const decodeUser = () => {
  try {
    const token = localStorage.getItem('accessToken');
    return token ? jwt_decode(token) : null;
  } catch {
    return null;
  }
};

export const isStaffRole = (role) =>
  ['company', 'manager', 'technical staff', 'auditor'].includes(String(role || '').toLowerCase());

const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
const VIDEO_EXT = /\.(mp4|mov|webm|avi|mkv)$/i;

export const mediaKind = (keyOrName) => {
  const s = String(keyOrName || '');
  if (IMAGE_EXT.test(s)) return 'image';
  if (VIDEO_EXT.test(s)) return 'video';
  return 'file';
};

export const fileNameFromKey = (key) => {
  const s = String(key || '');
  const idx = s.lastIndexOf('/');
  return idx >= 0 ? s.slice(idx + 1) : s;
};

export const liveAssignments = (assignments = []) =>
  (assignments || []).filter((a) => a && !['cancelled', 'declined'].includes(a.status));

/** "2 assigned · 1 pending approval · Approved $1,250.00" */
export const summarizeAssignments = (assignments = []) => {
  const list = Array.isArray(assignments) ? assignments : [];
  if (!list.length) return '';
  const counts = { assigned: 0, quoted: 0, approved: 0, declined: 0, cancelled: 0 };
  let approvedTotal = 0;
  list.forEach((a) => {
    if (!a) return;
    counts[a.status] = (counts[a.status] || 0) + 1;
    if (a.status === 'approved') {
      const amt = a.approval && a.approval.amount != null ? a.approval.amount : a.quote && a.quote.amount;
      if (amt != null && !Number.isNaN(Number(amt))) approvedTotal += Number(amt);
    }
  });
  const parts = [];
  if (counts.assigned) parts.push(`${counts.assigned} assigned`);
  if (counts.quoted) parts.push(`${counts.quoted} pending approval`);
  if (counts.approved) parts.push(`Approved ${fmtMoney(approvedTotal)}`);
  if (counts.declined) parts.push(`${counts.declined} declined`);
  if (!parts.length && counts.cancelled) parts.push(`${counts.cancelled} cancelled`);
  return parts.join(' · ');
};

export const collectMediaKeys = (doc) => {
  if (!doc) return [];
  const keys = [];
  (doc.cleanliness?.attachments || []).forEach((k) => k && keys.push(k));
  (doc.repairs || []).forEach((r) => (r?.attachments || []).forEach((k) => k && keys.push(k)));
  return Array.from(new Set(keys));
};

export const propertyLabel = (doc) => {
  const p = doc?.property_details?.property_id;
  if (!p) return '';
  if (typeof p === 'string') return '';
  return p.title || '';
};

export const isEmailLike = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

/** Accepts a string or an actor object ({ name } / { by_name }) and returns a display name. */
export const personName = (x) => {
  if (!x) return '';
  if (typeof x === 'string') return x;
  return x.name || x.by_name || x.email || x.by_email || '';
};

/** Escape for the rare place we hand HTML to SweetAlert. */
export const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
